// src/components/layout/Header.tsx
import { useState, useEffect } from 'react';
import { Menu, X, Phone, ArrowUpRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { scrollToTop } from '../../utils/scrollUtils';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Track scrolling for header styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => document.body.classList.remove('no-scroll');
  }, [isMobileMenuOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const navLinks = [
    { name: 'Domov', href: '/' },
    { name: 'O nas', href: '/o-nas' },
    { name: 'Kontakt', href: '/kontakt' },
    { name: 'Oprema', href: '/oprema' },
    { name: 'Intervencije', href: '/intervencije' },
    { name: 'Donacija dohodnine', href: '/donacija' },
  ];

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      scrollToTop();
    }
  };

  return (
    <>
      {/* Fixed Header */}
      <header
        className={`fixed top-1 left-0 right-0 z-50 transition-all duration-300 rounded-full mx-2 ${
          isScrolled ? 'glass py-3 shadow-sm' : 'bg-transparent py-3'
        }`}
        style={{ top: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            onClick={handleLogoClick}
            className="flex items-center bg-secondary/80 backdrop-blur-sm px-3 py-1 rounded-full md:px-3 md:py-1 sm:px-2 sm:py-1"
          >
            <img
              src="/assets/images/pgd-zp-logo-48.png"
              alt="PGD Zgornja Polskava Logo"
              width={32}
              height={32}
              className="mr-2"
            />
            <span className="text-lg font-sans font-semibold md:text-lg sm:text-base">
              <span className="text-primary">PGD</span>{' '}
              <span className="text-white">Zgornja Polskava</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-sans font-medium bg-secondary/80 backdrop-blur-sm px-3 py-1 rounded-full transition-all duration-300 hover:text-white hover:underline hover:underline-offset-4 ${
                  location.pathname === link.href || (link.href !== '/' && location.pathname.startsWith(link.href))
                    ? 'text-primary-foreground underline underline-offset-4'
                    : 'text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA Button */}
          <div className="hidden md:block">
            <Link
              to="/kontakt"
              className="btn-primary-rounded text-sm font-sans font-medium flex items-center space-x-1"
            >
              <span>Pridruži se</span>
              <ArrowUpRight size={16} />
            </Link>
          </div>

          {/* Mobile Menu Controls */}
          <div className="flex items-center space-x-4 md:hidden">
            {/* Phone CTA */}
            <a
              href="tel:+38640654321"
              className="ml-2 flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white transition-all duration-300 hover:scale-105 transform"
            >
              <Phone size={20} />
              <span className="sr-only">Pokliči nas</span>
            </a>
            {/* Hamburger Menu */}
            <button
              className="bg-secondary/80 backdrop-blur-sm p-2 rounded-full text-white"
              onClick={toggleMobileMenu}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              <span className="sr-only">{isMobileMenuOpen ? 'Zapri meni' : 'Odpri meni'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Separate Mobile Menu (outside the header) */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[100] bg-secondary/90 backdrop-blur-lg md:hidden flex flex-col"
          id="mobile-menu"
        >
          <div className="w-full px-4 py-6 flex justify-between items-center">
            <Link
              to="/"
              onClick={handleLogoClick}
              className="flex items-center bg-secondary/80 backdrop-blur-sm px-2 py-1 rounded-full"
            >
              <img
                src="/assets/images/pgd-zp-logo-48.png"
                alt="PGD Zgornja Polskava Logo"
                width={32}
                height={32}
                className="mr-2"
              />
              <span className="text-base font-sans font-semibold">
                <span className="text-primary">PGD</span>{' '}
                <span className="text-white">Zgornja Polskava</span>
              </span>
            </Link>
            <button
              className="bg-white/20 p-2 rounded-full text-white"
              onClick={toggleMobileMenu}
              aria-label="Zapri meni"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-4 py-10 overflow-y-auto">
            <div className="space-y-6 w-full max-w-md">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`block text-lg font-sans font-medium w-full text-center py-3 rounded-full transition-all duration-300 hover:text-white hover:bg-secondary hover:underline hover:underline-offset-4 ${
                    location.pathname === link.href || (link.href !== '/' && location.pathname.startsWith(link.href))
                      ? 'bg-primary text-white underline underline-offset-4'
                      : 'text-white'
                  }`}
                  onClick={toggleMobileMenu}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/kontakt"
                className="block w-full bg-primary text-white text-center py-4 rounded-full font-sans font-medium transition-all duration-300 hover:bg-primary-700 flex items-center justify-center space-x-1"
                onClick={toggleMobileMenu}
              >
                <span>Pridruži se</span>
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Spacer to prevent content from going under fixed header */}
      <div style={{ height: 'var(--header-height, 5rem)' }} />
    </>
  );
};

export default Header;